// @djsem/shared — product plan levels + build phases + feature gating map.
// Values come from the DJSEM-RESEARCH-IMPLEMENTATION-PACK-001 spec — nothing here is
// invented pricing/licensing (hard rule 10): these are access TIERS, not prices.
import { vString, type Validator } from "./validation";

/** Ordered list of the canonical DJSEM plan/access tiers, from lowest to highest. */
export const PLAN_LEVELS = [
  "free_starter", "creator", "creator_pro", "performer",
  "pro_integrations", "venue", "enterprise",
] as const;
/** A DJSEM product plan/access tier (billing-authoritative, 7 values). */
export type DJSEMPlanLevel = (typeof PLAN_LEVELS)[number];

/** Ordered list of the canonical build phases a feature can be scoped to. */
export const BUILD_PHASES = [
  "poc", "mvp", "v1", "v2", "pro_integrations", "venue_enterprise", "research_only", "reject",
] as const;
/** The build phase a feature is scoped to (poc/mvp/v1/v2/etc). */
export type BuildPhase = (typeof BUILD_PHASES)[number];
/** Prompt 03 canonical name — same values as {@link BuildPhase}. */
export type DJSEMBuildPhase = BuildPhase;

/** Validator asserting a value is one of the canonical {@link PLAN_LEVELS}. */
export const vPlanLevel: Validator<DJSEMPlanLevel> = vString({ enum: PLAN_LEVELS }) as Validator<DJSEMPlanLevel>;
/** Validator asserting a value is one of the canonical {@link BUILD_PHASES}. */
export const vBuildPhase: Validator<BuildPhase> = vString({ enum: BUILD_PHASES }) as Validator<BuildPhase>;

/**
 * Feature → minimum plan level (verbatim from the spec's product plan mapping). Maps each
 * gated feature key to the lowest plan tier that unlocks it.
 */
export const DJSEM_FEATURE_PLAN_MAP = {
  chapter_editor: "creator",
  cue_point_review: "creator",
  smart_groups: "creator",
  intelligent_sort: "creator",
  set_builder: "creator",
  transition_designer: "creator_pro",
  visual_scene_engine_lite: "creator",
  lighting_scene_simulation: "creator_pro",
  stem_intelligence_metadata: "creator_pro",
  rehearsal_mode: "performer",
  live_conduct_controls: "performer",
  pro_output_adapter_dry_run: "pro_integrations",
  venue_profile_editor: "venue",
  real_hardware_adapters: "enterprise",
  marketplace_public_sales: "v2",
} as const satisfies Record<string, DJSEMPlanLevel | BuildPhase>;
/** Key of a feature gated by {@link DJSEM_FEATURE_PLAN_MAP}. */
export type DJSEMFeatureKey = keyof typeof DJSEM_FEATURE_PLAN_MAP;
